import { createStore, createEvent, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { routePathNameChanged } from "@features/app/model";
import { PageTypes } from "@features/shared/page-type/page-types";
import { $firstAssistantAnswerCompleted, toggleSelector } from "@features/ai-tools/model";
import { IAssistantModelContract } from "@features/assistant/model";
import { IUpsellUIContract } from "@features/upsell/ui";
import { IAiToolsPackContract } from "@features/upsell/ui/ai-tools-pack";

export const $backToLessonBlockVisible = createStore<boolean>(false);
export const $stayInChatBubbleShow = createStore<boolean>(false);

const chatListOpened = createEvent();

const contract = new ModelContract({ $backToLessonBlockVisible, $stayInChatBubbleShow }, { chatListOpened }, {}, {});

export type IAssistantUIContract = typeof contract;

export class AssistantUIModule extends ModuleDescription<IAssistantUIContract> {
    constructor(
        public routerModule: IRouterContract,
        public assistantModelModule: IAssistantModelContract,
        public aiToolsPackModule: IAiToolsPackContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: [this.routerModule.stores.$historyTyped, $firstAssistantAnswerCompleted],
            source: {
                history: this.routerModule.stores.$historyTyped,
                firstAnswerCompleted: $firstAssistantAnswerCompleted
            },
            fn: ({ history, firstAnswerCompleted }) => {
                const prevType = history.at(-2)?.page?.type ?? "unknown";
                const isPrevLearning = prevType === PageTypes.CHALLENGE || prevType === PageTypes.LESSON;
                return isPrevLearning && firstAnswerCompleted;
            },
            target: $backToLessonBlockVisible
        });

        sample({
            clock: [this.aiToolsPackModule.events.assistantPromptLimitReached],
            source: {
                history: this.routerModule.stores.$historyTyped
            },
            fn: ({ history }) => {
                const prevType = history.at(-2)?.page?.type ?? "unknown";
                const isPrevLearning = prevType === PageTypes.CHALLENGE || prevType === PageTypes.LESSON;
                return isPrevLearning;
            },
            target: $backToLessonBlockVisible
        });

        sample({
            clock: this.assistantModelModule.events.stayInChatClicked,
            fn: () => false,
            target: $backToLessonBlockVisible
        });

        sample({
            clock: this.assistantModelModule.events.stayInChatClicked,
            fn: () => true,
            target: $stayInChatBubbleShow
        });

        sample({
            clock: this.assistantModelModule.events.hideInChatBubble,
            fn: () => false,
            target: $stayInChatBubbleShow
        });

        sample({
            clock: [routePathNameChanged, toggleSelector, chatListOpened],
            fn: () => false,
            target: $stayInChatBubbleShow
        });
    }
}
